import ActionTypes from "../actionTypes";
import { findActiveRecurringSubscription } from "../../../utils/subscriptionUtils";
import logger from "../../../utils/logger";


// Set subscriptions data
export const setSubscriptions = subscriptions => ({
  type: ActionTypes.SET_SUBSCRIPTIONS,
  payload: subscriptions,
});

// Set subscriptions loading state
export const setSubscriptionsLoading = loading => ({
  type: ActionTypes.SET_SUBSCRIPTIONS_LOADING,
  payload: loading,
});

// Set subscriptions error
export const setSubscriptionsError = error => ({
  type: ActionTypes.SET_SUBSCRIPTIONS_ERROR,
  payload: error,
});

// Clear subscriptions data
export const clearSubscriptions = () => {
  logger.log("JGNGNNFM: clearSubscriptions action dispatched");
  return {
    type: ActionTypes.CLEAR_SUBSCRIPTIONS,
  };
};

// Set active subscription
export const setActiveSubscription = subscription => ({
  type: ActionTypes.SET_ACTIVE_SUBSCRIPTION,
  payload: subscription,
});

// Set subscription wall checked status
export const setSubscriptionWallChecked = checked => ({
  type: ActionTypes.SET_SUBSCRIPTION_WALL_CHECKED,
  payload: checked,
});

// Set feature flags
export const setFeatureFlags = featureFlags => ({
  type: ActionTypes.SET_FEATURE_FLAGS,
  payload: featureFlags,
});

// Set feature flags loading state
export const setFeatureFlagsLoading = loading => ({
  type: ActionTypes.SET_FEATURE_FLAGS_LOADING,
  payload: loading,
});

// Set feature flags error
export const setFeatureFlagsError = error => ({
  type: ActionTypes.SET_FEATURE_FLAGS_ERROR,
  payload: error,
});

// Clear feature flags
export const clearFeatureFlags = () => ({
  type: ActionTypes.CLEAR_FEATURE_FLAGS,
});

// Set applicable package
export const setApplicablePackage = packageData => ({
  type: ActionTypes.SET_APPLICABLE_PACKAGE,
  payload: packageData,
});

// Set applicable package loading state
export const setApplicablePackageLoading = loading => ({
  type: ActionTypes.SET_APPLICABLE_PACKAGE_LOADING,
  payload: loading,
});

// Set applicable package error
export const setApplicablePackageError = error => ({
  type: ActionTypes.SET_APPLICABLE_PACKAGE_ERROR,
  payload: error,
});

// Clear applicable package
export const clearApplicablePackage = () => ({
  type: ActionTypes.CLEAR_APPLICABLE_PACKAGE,
});

// Async action to fetch subscriptions
export const fetchSubscriptions = organizationId => {
  return async dispatch => {
    try {
      dispatch(setSubscriptionsLoading(true));
      dispatch(setSubscriptionsError(null));

      // Import billingService dynamically to avoid circular imports
      const { default: billingService } = await import("../../../services/billingService");

      const response = await billingService.getOrganizationSubscriptions({
        organizationId,
        limit: 50,
        status: "USABLE",
        orderBy: "subscriptionDate:desc",
      });

      const subscriptions = response.data?.data || [];
      dispatch(setSubscriptions(subscriptions));

      // Always prefer recurring subscriptions when determining the active package
      const activeSubscription = findActiveRecurringSubscription(subscriptions);

      dispatch(setActiveSubscription(activeSubscription || null));

      return { subscriptions, activeSubscription };
    } catch (error) {
      logger.error("Error fetching subscriptions:", error);
      dispatch(setSubscriptionsError(error.message || "Failed to fetch subscriptions"));
      throw error;
    } finally {
      dispatch(setSubscriptionsLoading(false));
    }
  };
};

// Check if user requires subscription based on org type and role
export const requiresSubscription = (orgTypeCode, roleCode) => {
  // Platform admin (ROLE_ADMINISTRATOR) - no subscription needed
  if (roleCode === "ROLE_ADMINISTRATOR") {
    return false;
  }

  // Host Property Management individuals don't require subscription
  if (roleCode === "ROLE_HOST_CUSTOMER_INDIVIDUAL") {
    return false;
  }

  // HOST_PROPERTY_MANAGEMENT and CORPORATE_CUSTOMER require subscription
  if (orgTypeCode === "HOST_PROPERTY_MANAGEMENT" || orgTypeCode === "CORPORATE_CUSTOMER") {
    return true;
  }

  // HOST_CORPORATE_CUSTOMER does NOT require subscription
  // Roles: ROLE_HOST_CUSTOMER_OWNER (admin), ROLE_HOST_CUSTOMER_APPROVER_EMPLOYEE (sub-admin), ROLE_HOST_CUSTOMER_EMPLOYEE (non-admin), ROLE_HOST_CUSTOMER_INDIVIDUAL
  // HOST_CUSTOMER_INDIVIDUAL does NOT require subscription

  // HOST_PROPERTY_DISTRIBUTION does NOT require subscription
  // Roles: ROLE_HOST (admin), ROLE_HOST_OPERATIONS (non-admin)
  // CORPORATE_CUSTOMER does NOT require subscription
  // All other org types do NOT require subscription
  return false;
};

// Check subscription wall status
export const checkSubscriptionWall = user => {
  return async dispatch => {
    try {
      const { organization, organizationRole } = user;
      const orgTypeCode = organization?.type?.code;
      const roleCode = organizationRole?.code;

      // Check if user requires subscription
      const needsSubscription = requiresSubscription(orgTypeCode, roleCode);

      if (!needsSubscription) {
        dispatch(setSubscriptionWallChecked(true));
        return { requiresSubscription: false, hasActiveSubscription: true };
      }

      // Fetch subscriptions to check for active subscription
      const { subscriptions, activeSubscription } = await dispatch(fetchSubscriptions(organization.id));

      const hasActiveSubscription = !!activeSubscription;

      dispatch(setSubscriptionWallChecked(true));

      return {
        requiresSubscription: true,
        hasActiveSubscription,
        subscriptions,
        activeSubscription,
      };
    } catch (error) {
      logger.error("Error checking subscription wall:", error);
      dispatch(setSubscriptionWallChecked(false));
      throw error;
    }
  };
};
